import React, { type CSSProperties, useId, useRef } from 'react';
import cn from 'clsx';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import CrossIcon from '@pkgs/components/assets/icons/xsmall/Cross-Naked.svg';
import ArrowDown from '@pkgs/components/assets/icons/xsmall/Arrow-Thin-Down.svg';
import { VisuallyHidden } from '@ev-tech/ui-core-components/VisuallyHidden';
import { Link } from '@ev-tech/ui-core-components/Link';
import { Copy } from '@ev-tech/ui-core-components/Copy';
import { Button } from '@ev-tech/ui-core-components/Button';
import { type BigDecimalRangeDto } from '@ev-tech/search-modules-api';

import { Popover, PopoverClose, PopoverRoot, PopoverTrigger } from '../Popover/Popover';

import styles from './RangeDropdown.module.css';

interface RangeDropdownProps {
  children: React.ReactNode;
  label: string;
  value: string;
  range?: BigDecimalRangeDto;
  className?: string;
  maxWidth?: number;
  onDone: () => void;
  onReset: () => void;
  'data-testid'?: string;
}

export const RangeDropdown = ({
  label,
  value,
  range,
  children,
  maxWidth = 100,
  onDone,
  onReset,
  className,
  'data-testid': dataTestId,
}: RangeDropdownProps) => {
  const ariaLabelledBy = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useAppContext();

  const onClearClick = (e: MouseEvent | KeyboardEvent) => {
    e.stopPropagation();
    if ((e as KeyboardEvent).key === 'Enter' || e.type === 'click') {
      onReset();
    }
  };

  const hasRangeValue = Boolean(range?.max) || Boolean(range?.min);
  const buttonStyle = {
    '--range-dropdown-value-max-width': `${maxWidth}px`,
  } as CSSProperties;

  return (
    <PopoverRoot modal="trap-focus">
      <div ref={containerRef}>
        <VisuallyHidden id={ariaLabelledBy}>
          <Copy
            as="span"
            text={t('search.select.hiddenAccessibilityText', {
              label,
            })}
          />
        </VisuallyHidden>
        <PopoverTrigger
          render={
            <button
              className={cn(styles.button, className)}
              data-testid={dataTestId}
              data-has-value={Boolean(value)}
              aria-labelledby={ariaLabelledBy}
              style={buttonStyle}
            >
              {value ? (
                <>
                  <Copy className={styles.text} data-testid={`${dataTestId}_value`} size="l" as="span" text={value} />
                  <CrossIcon
                    className={styles.crossIcon}
                    tabIndex={0}
                    data-testid={`${dataTestId}_clear_button`}
                    aria-label={t('search.searchbar.reset')}
                    onClick={onClearClick}
                    onKeyDown={onClearClick}
                  />
                </>
              ) : (
                <>
                  <Copy className={styles.label} size="l" as="span" text={label} />
                  <ArrowDown className={styles.arrowDown} />
                </>
              )}
            </button>
          }
        />
        <Popover aria-label={label} className={styles.dialog} portalContainer={containerRef} positionMethod="fixed">
          <div className={styles.dialogBody}>{children}</div>
          <hr className={styles.divider} />
          <div className={styles.buttonsContainer} data-has-value={hasRangeValue}>
            {hasRangeValue && <Link as="button" label={t('search.searchbar.reset')} size="small" onClick={onReset} />}
            <PopoverClose
              render={
                <Button
                  variant="primary"
                  label={t('search.searchbar.price.button.apply')}
                  size="small"
                  testId={`${dataTestId}_doneButton`}
                  onClick={onDone}
                />
              }
            />
          </div>
        </Popover>
      </div>
    </PopoverRoot>
  );
};
